import {
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { deleteObject, ref as storageRef } from 'firebase/storage';
import { db, storage } from '../firebase';

/**
 * Stable gallery doc id for a manifest-queue asset (must match Cloud Functions).
 * @param {string} manifestId
 * @param {string} assetId
 * @returns {string}
 */
export function galleryDocIdForManifestAsset(manifestId, assetId) {
  const m = String(manifestId || '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 40);
  const a = String(assetId || '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 40);
  return `mq_${m}_${a}`.slice(0, 120);
}

/**
 * @param {string} companyId
 * @param {string} projectId
 * @param {string} galleryDocId
 */
function galleryAssetDoc(companyId, projectId, galleryDocId) {
  return doc(
    db,
    'companies',
    companyId,
    'game_projects',
    projectId,
    'assets',
    galleryDocId,
  );
}

/**
 * Best-effort Storage cleanup.
 * @param {string|null|undefined} storagePath
 */
async function deleteStoragePathBestEffort(storagePath) {
  const path = typeof storagePath === 'string' ? storagePath.trim() : '';
  if (!path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch (err) {
    console.warn('gallery storage delete skipped:', path, err);
  }
}

/**
 * Upsert / replace a gallery card for a Render Queue asset.
 * @param {{
 *   companyId: string,
 *   projectId: string,
 *   manifestId: string,
 *   asset: object,
 *   replaceStorage?: boolean,
 *   uid?: string|null,
 * }} params
 */
export async function upsertProjectGalleryAssetClient(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const projectId =
    typeof params.projectId === 'string' ? params.projectId.trim() : '';
  const manifestId =
    typeof params.manifestId === 'string' ? params.manifestId.trim() : '';
  const asset = params.asset && typeof params.asset === 'object' ? params.asset : {};
  const assetId =
    typeof asset.id === 'string' && asset.id.trim() ? asset.id.trim() : '';
  if (!companyId || !projectId || !manifestId || !assetId) return '';

  const galleryId = galleryDocIdForManifestAsset(manifestId, assetId);
  const ref = galleryAssetDoc(companyId, projectId, galleryId);
  const snap = await getDoc(ref);
  const previous = snap.exists() ? snap.data() || {} : null;

  const imageUrl =
    (typeof asset.previewUrl === 'string' && asset.previewUrl.trim()) ||
    (typeof asset.imageUrl === 'string' && asset.imageUrl.trim()) ||
    null;
  const storagePath =
    typeof asset.storagePath === 'string' && asset.storagePath.trim()
      ? asset.storagePath.trim()
      : null;

  if (
    params.replaceStorage &&
    previous &&
    typeof previous.storagePath === 'string' &&
    previous.storagePath.trim() &&
    previous.storagePath.trim() !== storagePath
  ) {
    await deleteStoragePathBestEffort(previous.storagePath);
  }

  /** @type {Record<string, unknown>} */
  const payload = {
    assetName:
      (typeof asset.name === 'string' && asset.name.trim()) ||
      (typeof asset.assetName === 'string' && asset.assetName.trim()) ||
      assetId,
    type:
      typeof asset.type === 'string' && asset.type.trim()
        ? asset.type.trim()
        : 'sprite',
    category:
      typeof asset.category === 'string' && asset.category.trim()
        ? asset.category.trim()
        : '',
    imageUrl,
    storagePath,
    promptUsed: typeof asset.prompt === 'string' ? asset.prompt.trim() : '',
    status:
      typeof asset.status === 'string' && asset.status.trim()
        ? asset.status.trim()
        : 'draft',
    manifestId,
    manifestAssetId: assetId,
    source: 'manifest_queue',
    updatedAt: serverTimestamp(),
    updatedByUid: params.uid || null,
  };

  const sourceAssetId =
    typeof asset.sourceAssetId === 'string' && asset.sourceAssetId.trim()
      ? asset.sourceAssetId.trim()
      : '';
  if (sourceAssetId) {
    payload.sourceAssetId = sourceAssetId;
  }

  if (!snap.exists()) {
    payload.createdAt = serverTimestamp();
    payload.createdByUid = params.uid || null;
  }

  await setDoc(ref, payload, { merge: true });
  return galleryId;
}

/**
 * Delete gallery card + storage for a queue asset.
 * @param {{
 *   companyId: string,
 *   projectId: string,
 *   manifestId: string,
 *   assetId: string,
 *   storagePath?: string|null,
 * }} params
 */
export async function deleteProjectGalleryAssetClient(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const projectId =
    typeof params.projectId === 'string' ? params.projectId.trim() : '';
  const manifestId =
    typeof params.manifestId === 'string' ? params.manifestId.trim() : '';
  const assetId =
    typeof params.assetId === 'string' ? params.assetId.trim() : '';
  if (!companyId || !projectId || !manifestId || !assetId) return;

  const galleryId = galleryDocIdForManifestAsset(manifestId, assetId);
  const ref = galleryAssetDoc(companyId, projectId, galleryId);
  const snap = await getDoc(ref);
  let storagePath =
    typeof params.storagePath === 'string' ? params.storagePath.trim() : '';
  if (snap.exists()) {
    const data = snap.data() || {};
    if (!storagePath && typeof data.storagePath === 'string') {
      storagePath = data.storagePath.trim();
    }
    await deleteDoc(ref);
  }
  await deleteStoragePathBestEffort(storagePath);
}
